import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { useAuth } from '@/hooks/useAuth';
import {
  deleteReview,
  fetchReviewStats,
  listReports,
  listReviews,
  markReviewHelpful,
  moderateReview,
  replyToReview,
  resolveReport,
  reviewEntityMeta,
  reviewStatusMeta,
  REPORT_REASONS,
  REVIEW_ENTITY_TYPES,
  type CoreReview,
  type CoreReviewReport,
  type ReviewStats,
} from '@/lib/coreReviews';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function Stars({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-accent-500">
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= value ? 'ri-star-fill' : 'ri-star-line opacity-40'}></i>
      ))}
    </span>
  );
}

export default function ReviewsTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<CoreReview[]>([]);
  const [reports, setReports] = useState<CoreReviewReport[]>([]);
  const [stats, setStats] = useState<ReviewStats>({ total: 0, published: 0, pending: 0, rejected: 0, reports: 0, average: 0, byRating: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [replyingId, setReplyingId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [r, rep, s] = await Promise.all([
        listReviews(org.id, { entityType: typeFilter || undefined, status: statusFilter || undefined }),
        listReports(org.id),
        fetchReviewStats(org.id),
      ]);
      setReviews(r);
      setReports(rep);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const maxRating = useMemo(() => Math.max(1, ...Object.values(stats.byRating)), [stats.byRating]);

  const moderate = async (id: number, status: 'published' | 'rejected' | 'hidden') => {
    try { await moderateReview(id, status, null, user?.id ?? null, commerceId); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); }
  };

  const submitReply = async (id: number) => {
    if (!replyText.trim()) return;
    try { await replyToReview(id, replyText.trim(), user?.id ?? null); setReplyText(''); setReplyingId(null); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Avis</span> : infrastructure commune reliée à un utilisateur/client, une entreprise,
        un service/produit/réservation lorsque pertinent, avec modération, signalement et réponse du professionnel.
        Les <span className="font-medium text-foreground-600">avis (opinions)</span> sont tenus distincts des <span className="font-medium text-foreground-600">données factuelles</span>.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-foreground-900">{stats.average || '—'}</span>
            <Stars value={Math.round(stats.average)} />
          </div>
          <p className="text-xs text-foreground-500 mt-1">Note moyenne publiée ({stats.published} avis)</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <p className="text-lg font-bold text-accent-600">{stats.pending}</p>
          <p className="text-xs text-foreground-500">En attente de modération</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <p className="text-lg font-bold text-foreground-900">{stats.total}</p>
          <p className="text-xs text-foreground-500">Avis au total</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <p className="text-lg font-bold text-accent-600">{stats.reports}</p>
          <p className="text-xs text-foreground-500">Signalements ouverts</p>
        </div>
      </div>

      {/* Répartition par note */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-3">Répartition des notes</h4>
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((r) => (
            <div key={r} className="flex items-center gap-2">
              <span className="w-10 text-xs text-foreground-600 flex items-center gap-1">{r} <i className="ri-star-fill text-accent-500 text-[10px]"></i></span>
              <div className="flex-1 h-2 rounded-full bg-background-100 overflow-hidden">
                <div className="h-full bg-accent-500 transition-all" style={{ width: `${((stats.byRating[r] || 0) / maxRating) * 100}%` }}></div>
              </div>
              <span className="w-6 text-right text-xs text-foreground-500">{stats.byRating[r] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${INPUT} !w-auto`} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Tous les types</option>
          {REVIEW_ENTITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select className={`${INPUT} !w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="published">Publié</option>
          <option value="pending">En attente</option>
          <option value="rejected">Refusé</option>
          <option value="hidden">Masqué</option>
        </select>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">
          <i className="ri-refresh-line"></i> Actualiser
        </button>
      </div>

      {/* Liste des avis */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-chat-quote-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Avis reçus</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : reviews.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun avis pour le moment.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {reviews.map((r) => {
              const st = reviewStatusMeta(r.status);
              const ent = reviewEntityMeta(r.entity_type);
              return (
                <div key={r.id} className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                      <i className={ent.icon}></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900">{r.author_name || 'Anonyme'}</span>
                        <Stars value={r.rating || 0} />
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.className}`}>{st.label}</span>
                        {r.is_verified && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 flex items-center gap-1"><i className="ri-verified-badge-line"></i> vérifié</span>}
                        {r.is_factual && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">donnée factuelle</span>}
                        {(r.report_count || 0) > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 flex items-center gap-1"><i className="ri-flag-line"></i> {r.report_count}</span>}
                      </div>
                      {r.title && <p className="text-sm font-medium text-foreground-800 mt-1">{r.title}</p>}
                      {r.comment && <p className="text-sm text-foreground-600 mt-0.5">{r.comment}</p>}
                      <p className="text-[11px] text-foreground-400 mt-1">
                        {ent.label}{r.entity_id ? ` #${r.entity_id}` : ''} · {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                      </p>

                      {r.reply_body && (
                        <div className="mt-2 pl-3 border-l-2 border-primary-200 bg-primary-50/40 rounded-r-md p-2">
                          <p className="text-[11px] font-semibold text-primary-800 flex items-center gap-1"><i className="ri-reply-line"></i> Réponse du professionnel</p>
                          <p className="text-xs text-foreground-600 mt-0.5">{r.reply_body}</p>
                        </div>
                      )}

                      {replyingId === r.id ? (
                        <div className="mt-2 flex flex-col sm:flex-row gap-2">
                          <input className={INPUT} placeholder="Votre réponse publique…" value={replyText} onChange={(e) => setReplyText(e.target.value)} maxLength={400} />
                          <button onClick={() => submitReply(r.id)} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">Répondre</button>
                          <button onClick={() => { setReplyingId(null); setReplyText(''); }} className="px-3 py-2 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer">Annuler</button>
                        </div>
                      ) : (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {r.status !== 'published' && (
                            <button onClick={() => moderate(r.id, 'published')} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors">Publier</button>
                          )}
                          {r.status !== 'rejected' && (
                            <button onClick={() => moderate(r.id, 'rejected')} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-red-200 transition-colors">Refuser</button>
                          )}
                          {r.status !== 'hidden' && (
                            <button onClick={() => moderate(r.id, 'hidden')} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">Masquer</button>
                          )}
                          {!r.reply_body && (
                            <button onClick={() => { setReplyingId(r.id); setReplyText(''); }} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors">Répondre</button>
                          )}
                          <button onClick={() => markReviewHelpful(r.id, r.helpful_count || 0).then(load)} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap cursor-pointer flex items-center gap-1">
                            <i className="ri-thumb-up-line"></i> {r.helpful_count || 0}
                          </button>
                          <button onClick={() => deleteReview(r.id).then(load)} className="ml-auto w-7 h-7 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-600 transition-colors" title="Supprimer">
                            <i className="ri-delete-bin-6-line text-xs"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Signalements */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-red-100 text-red-700 flex items-center justify-center"><i className="ri-flag-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Signalements</h4>
        </div>
        {reports.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun signalement.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {reports.map((rep) => {
              const reason = REPORT_REASONS.find((x) => x.key === rep.reason)?.label || rep.reason;
              return (
                <div key={rep.id} className="flex items-center gap-3 px-4 py-2.5">
                  <i className="ri-flag-2-line text-red-500"></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground-800 truncate">{reason} <span className="text-foreground-400">· avis #{rep.review_id}</span></p>
                    <p className="text-[11px] text-foreground-400 truncate">{rep.detail || '—'} · {rep.reporter_name || 'anonyme'}</p>
                  </div>
                  {rep.status === 'open' ? (
                    <div className="flex gap-1.5">
                      <button onClick={() => resolveReport(rep.id, 'resolved').then(load)} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer">Traiter</button>
                      <button onClick={() => resolveReport(rep.id, 'dismissed').then(load)} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap cursor-pointer">Rejeter</button>
                    </div>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">{rep.status === 'resolved' ? 'Traité' : 'Rejeté'}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}